/**
 * "Export CSV" for every list in the console (docs/06 §4). It calls `rows()` — which pages
 * through the API with the screen's own filters (`lib/exportAll`) — and then says how many rows
 * it wrote, so a truncated export can never pass unnoticed again (QA 2026-09-14 CMS-21/CMS-22).
 *
 * The export is capped at `EXPORT_MAX_ROWS`, and the cap used to be silent: /songs wrote 20,000
 * rows against a table reading 143,392 and reported "20000 rows exported" (CMS-68). When
 * `rows()` returns an `ExportResult` the button now names the cap and the filtered total, and
 * it counts the rows up while the 100 round trips run so the 5 minutes are not a frozen screen.
 */
import { useState } from 'react';

import { useT } from '../../i18n';
import { type CsvColumn, downloadCsv, toCsv } from '../../lib/csv';
import { EXPORT_MAX_ROWS, type ExportResult } from '../../lib/exportAll';
import { Button } from '../ui/button';

export interface ExportCsvButtonProps<T> {
  filename: string;
  columns: readonly CsvColumn<T>[];
  /**
   * The rows to write. Screens pass a `fetchAllRowsResult` call over the current filters and
   * hand it `onProgress`; a plain array (or a promise of one) still works for the lists that
   * hold every row already.
   */
  rows: (
    onProgress: (fetched: number, total: number) => void,
  ) => T[] | ExportResult<T> | Promise<T[] | ExportResult<T>>;
  label?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

function normalize<T>(value: T[] | ExportResult<T>): ExportResult<T> {
  return Array.isArray(value) ? { rows: value, total: value.length, truncated: false } : value;
}

export function ExportCsvButton<T>({
  filename,
  columns,
  rows,
  label,
  disabled,
  'data-testid': testId = 'export-csv',
}: ExportCsvButtonProps<T>) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [fetched, setFetched] = useState(0);
  const [result, setResult] = useState<ExportResult<T> | null>(null);
  const [failed, setFailed] = useState(false);

  const run = async () => {
    setBusy(true);
    setFailed(false);
    setResult(null);
    setFetched(0);
    try {
      const data = normalize(await rows((rowsSoFar) => setFetched(rowsSoFar)));
      downloadCsv(filename, toCsv(data.rows, columns));
      setResult(data);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="flex items-center gap-2">
      <Button
        variant="secondary"
        size="sm"
        data-testid={testId}
        disabled={busy || disabled}
        onClick={() => void run()}
      >
        {busy ? t('cms:table.exporting') : (label ?? t('cms:table.export'))}
      </Button>
      {busy && fetched > 0 && (
        <span className="text-[12px] text-muted" role="status" data-testid={`${testId}-progress`}>
          {t('cms:table.exportProgress', { rows: fetched })}
        </span>
      )}
      {result !== null && (
        <span className="text-[12px] text-muted" data-testid={`${testId}-count`}>
          {t('cms:table.exported', { count: result.rows.length })}
        </span>
      )}
      {result?.truncated && (
        <span
          className="text-[12px] text-warn-ink"
          role="status"
          data-testid={`${testId}-truncated`}
        >
          {t('cms:table.exportTruncated', { max: EXPORT_MAX_ROWS, total: result.total })}
        </span>
      )}
      {failed && (
        <span className="text-[12px] text-danger" role="alert">
          {t('common:states.error')}
        </span>
      )}
    </span>
  );
}
